// LiveStream.js
import { connect } from 'react-redux';
import withRouter from '../component/WithRouter.jsx';
import { goBack } from 'redux-first-history';

import { DrmFeatureName } from '../resource/drmConstants.js';

import LiveStream from '../component/LiveStream.jsx';

import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMeData from '../selector/getMeData.js';
import getUserData from '../selector/getUserData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';
import getIsPrivateShowUnlocked from '../selector/getIsPrivateShowUnlocked.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getDrmKeySystemAndRobustness from '../selector/getDrmKeySystemAndRobustness.js';
import getShouldShowLivestreamCta from '../selector/getShouldShowLivestreamCta.js';
import getIsPermissionMatched from '../selector/getIsPermissionMatched.js';
import getPublicUrlHostname from '../selector/getPublicUrlHostname.js';
import {
  getHasLivestreamTopGift,
  getIsExclusiveViewerInMainWindow,
  getShouldUseOverlayCta,
  getViewerCameraData,
  getIsMeExclusivePrivateInviteInitiator,
  getIsMeExclusivePrivatePerformingInitiator,
} from '../selector/getLivestreamComputedData.js';

import fetchUser from '../action/fetchUser.js';
import removeModals from '../action/removeModals.js';
import addModal from '../action/addModal.js';
import resetLiveStream from '../action/resetLiveStream.js';
import toggleModal from '../action/toggleModal.js';
import fetchStreamsToken from '../action/fetchStreamsToken.js';
import updateLiveStreamPreset from '../action/updateLiveStreamPreset.js';
import setPlayerData from '../action/setPlayerData.js';
import fetchRetainedEvents from '../action/fetchRetainedEvents.js';
import setShouldDisplayCleanModeHint from '../action/setShouldDisplayCleanModeHint.js';
import setShouldDisplayHorizonLayoutHint from '../action/setShouldDisplayHorizonLayoutHint.js';
import setShouldDisplayAsideModuleHint from '../action/setShouldDisplayAsideModuleHint.js';

import { ExclusivePrivateStatus } from '../resource/liveStreamConstants.js';

import {
  LIVESTREAM_PUBLIC_SOURCE_FORMAT,
  LIVESTREAM_PRIVATE_SOURCE_FORMAT,
  LIVESTREAM_OPERATION_HINT,
  LIVESTREAM_EXIT_ALERT,
  JWT_SCOPE_LIVESTREAM_MODERATOR,
  FEATURE_LIVESTREAM_SNAPSHOT,
} from '../RemoteConfigKeys.js';
import { breakpoint } from '../style/variables.js';
import {
  MERGE_OPERATION_DATA,
  REMOVE_LIVE_STREAM_DATA,
} from '../ActionTypes.js';

const mapStateToProps = (state, { userId, streamId }) => {
  const preset = getLiveStreamData(state, [streamId], 'preset');
  const meId = getMeData(state, 'id');
  const fundingId = getLiveStreamData(
    state,
    [streamId, 'privateShow'],
    'fundingId'
  );
  const privateShowStatus = getPrivateShowStatus(state, streamId);

  const isPrivateShowUnlocked = getIsPrivateShowUnlocked(
    state,
    streamId,
    fundingId,
    meId
  );
  const isPrivateStreamUnlocked = getOperationData(
    state,
    ['liveStream', 'paid', streamId],
    'isPrivateStreamUnlocked'
  );
  const streamStatus = getLiveStreamData(state, [streamId], 'status');

  const exclusivePrivateStatus = getLiveStreamData(
    state,
    [streamId, 'exclusivePrivate'],
    'status'
  );

  const publicUrlHostname = getPublicUrlHostname(state);

  const deviceWidth = getOperationData(state, ['device'], 'width');
  const isOnMobile = deviceWidth <= breakpoint.tablet;

  const isPrivateStreamSubscribed = getOperationData(
    state,
    ['pusher', 'channels'],
    `private-enc-stream@${streamId}`
  )?.subscribed;

  const { keySystem } = getDrmKeySystemAndRobustness(
    state,
    DrmFeatureName.LIVESTREAM
  );

  const isMeExclusivePrivatePerformingInitiator =
    getIsMeExclusivePrivatePerformingInitiator(state, streamId);
  const isExclusivePrivatePerforming =
    ExclusivePrivateStatus.PERFORMING === exclusivePrivateStatus;
  const isExclusivePrivatePreparing =
    ExclusivePrivateStatus.PREPARING === exclusivePrivateStatus;
  const byteplusToken = getOperationData(
    state,
    ['liveStream', 'paid', streamId],
    'byteplusToken'
  );
  const hasStreamToken = !!(
    byteplusToken ||
    getOperationData(state, ['liveStream', 'paid', streamId], 'streamToken')
  );
  const sessionId = getLiveStreamData(state, [streamId], 'sessionId');

  // exclusive private (1v1) viewer camera states — camera-on covers the PREPARING
  // self-view preview as well as the live PERFORMING publish.
  const isViewerCameraPreviewReady = getViewerCameraData(
    state,
    streamId,
    'isPreviewReady'
  );
  const hasViewerCameraMediaAccessError = getViewerCameraData(
    state,
    streamId,
    'hasMediaAccessError'
  );

  return {
    meId,
    userId,
    fundingId,
    drmTestStatus: getOperationData(state, ['drm'], 'testStatus'),
    privateShowStatus,
    streamStatus,
    isGiftPanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isGiftPanelOpen'
    ),
    isChatPanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isChatPanelOpen'
    ),
    isTriggerPrivatePanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isTriggerPrivatePanelOpen'
    ),
    isTopLeaderboardScrolling: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isTopLeaderboardScrolling'
    ),
    isAsideExpanded: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isAsideExpanded'
    ),
    isPinnedMessageExpanded: getOperationData(
      state,
      ['liveStream', 'pinnedMessage', sessionId],
      'isExpanded'
    ),
    deviceHeight: getOperationData(state, ['device'], 'height'),
    preset,
    username: getUserData(state, userId, 'username'),
    isLiveStreaming: getLiveStreamData(state, [streamId], 'isLiveStreaming'),
    isPrivateShowUnlocked,
    isPrivateStreamUnlocked,
    isMeExclusivePrivateInviteInitiator: getIsMeExclusivePrivateInviteInitiator(
      state,
      streamId
    ),
    isPrivateStreamSubscribed,
    sourceId: getLiveStreamData(state, [streamId], 'sourceId'),
    hasTopGift: getHasLivestreamTopGift(state, streamId),
    isExclusiveViewerInMainWindow: getIsExclusiveViewerInMainWindow(
      state,
      streamId
    ),
    isViewerCameraPreviewReady,
    hasViewerCameraMediaAccessError,
    provider: getLiveStreamData(state, [streamId], 'provider'),
    isOnMobile,
    keySystem,
    publicSourceFormat: getRemoteConfigData(
      state,
      LIVESTREAM_PUBLIC_SOURCE_FORMAT
    ),
    privateSourceFormat: getRemoteConfigData(
      state,
      LIVESTREAM_PRIVATE_SOURCE_FORMAT
    ),
    shouldShowLivestreamCta: getShouldShowLivestreamCta(state, streamId),
    hasScrollHintDisplayed: getMeData(state, 'hasScrollHintDisplayed'),
    shouldShowOperationHintUI: getRemoteConfigData(
      state,
      LIVESTREAM_OPERATION_HINT
    ),
    shouldShowExitAlertModal: getRemoteConfigData(state, LIVESTREAM_EXIT_ALERT),
    shouldUseOverlayCta: getShouldUseOverlayCta(state, streamId),
    isExclusivePrivatePerforming,
    isExclusivePrivatePreparing,
    isMeExclusivePrivatePerformingInitiator,
    isExclusivePrivateUnlocked:
      !isExclusivePrivatePerforming ||
      isMeExclusivePrivatePerformingInitiator ||
      hasStreamToken,
    hasStreamToken,
    isLivestreamModerator: getIsPermissionMatched(
      state,
      JWT_SCOPE_LIVESTREAM_MODERATOR
    ),
    shouldShowRetryPlayButton: getOperationData(state, [
      'liveStream',
      'shouldShowRetryPlayButton',
    ]),
    shouldDisplayCleanModeHint: getOperationData(
      state,
      ['liveStream'],
      'shouldDisplayCleanModeHint'
    ),
    shouldDisplayHorizonLayoutHint: getOperationData(
      state,
      ['liveStream'],
      'shouldDisplayHorizonLayoutHint'
    ),
    shouldDisplayAsideModuleHint: getOperationData(
      state,
      ['liveStream'],
      'shouldDisplayAsideModuleHint'
    ),
    videoHeight: getLiveStreamData(state, [streamId], 'videoHeight'),
    videoWidth: getLiveStreamData(state, [streamId], 'videoWidth'),
    playerStatus: getLiveStreamData(state, [streamId], 'playerStatus'),
    sessionId: getLiveStreamData(state, [streamId], 'sessionId'),
    isLiveStreamSnapshotEnabled: getRemoteConfigData(
      state,
      FEATURE_LIVESTREAM_SNAPSHOT
    ),
    publicUrlHostname,
  };
};

const mapDispatchToProps = (dispatch, { userId }) => {
  return {
    fetchUser: ({ userId }) => dispatch(fetchUser({ id: userId })),
    closeModal: ({ ids }) =>
      dispatch(
        removeModals({
          ids,
          transitionStatus: 'closing',
        })
      ),
    openModal: ({ id }) =>
      dispatch(
        addModal({ id, isHigherThanAll: true, transitionStatus: 'coming' })
      ),
    toggleModal: data => dispatch(toggleModal(data)),
    resetLiveStream: () =>
      dispatch(resetLiveStream({ streamId: userId, isLeaveLivestream: true })),
    goBack: () => dispatch(goBack()),
    fetchStreamsToken: ({ streamId }) =>
      dispatch(fetchStreamsToken({ streamId })),
    updateLiveStreamPreset: ({ streamId, preset, pusherPreset, reason }) =>
      dispatch(
        updateLiveStreamPreset({ streamId, preset, pusherPreset, reason })
      ),
    setShouldShowRetryPlayButton: ({ shouldShow }) =>
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['liveStream'],
          data: {
            shouldShowRetryPlayButton: shouldShow,
          },
        },
      }),
    setPlayerData: data => dispatch(setPlayerData(data)),
    cleanupAgoraToken: ({ streamId }) =>
      dispatch({
        type: REMOVE_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId, 'providerInfo', 'agora'],
        },
      }),
    fetchRetainedEvents: ({ channels }) =>
      dispatch(fetchRetainedEvents({ channels })),
    setShouldDisplayCleanModeHint: ({ shouldDisplayCleanModeHint }) =>
      dispatch(setShouldDisplayCleanModeHint({ shouldDisplayCleanModeHint })),
    setShouldDisplayHorizonLayoutHint: ({ shouldDisplayHorizonLayoutHint }) =>
      dispatch(
        setShouldDisplayHorizonLayoutHint({ shouldDisplayHorizonLayoutHint })
      ),
    setShouldDisplayAsideModuleHint: ({ shouldDisplayAsideModuleHint }) =>
      dispatch(
        setShouldDisplayAsideModuleHint({ shouldDisplayAsideModuleHint })
      ),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LiveStream)
);
